import React from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

interface VideoJSOptions {
    controls: boolean;
    responsive: boolean;
    fluid: boolean;
    autoplay: boolean;
    controlBar: {
        volumePanel: {
            inline: boolean;
        };
        children: Array<string>
    };
    sources: Array<{
        src: string;
        type: string;
    }>;
    poster?: string;
    tracks?: Array<{
        src: string;
        kind?: string;
        srclang?: string;
        label?: string;
        default?: boolean;
    }>;
}

interface VideoJSProps {
    source: string;
    poster?: string;
    onReady?: (player: any) => void;
    captions?: {
        src: string;
        kind?: 'subtitles' | 'captions' | 'descriptions' | 'chapters' | 'metadata';
        srclang?: string;
        label?: string;
        default?: boolean;
    } | string;
}

export const VideoJS = (props: VideoJSProps) => {
    const videoRef = React.useRef<HTMLDivElement>(null);
    const playerRef = React.useRef<any>(null);
    const blobUrlRef = React.useRef<string | null>(null);

    const createTracks = (): VideoJSOptions['tracks'] => {
        if (!props.captions) return [];

        if (typeof props.captions === 'string') {
            const blob = new Blob([props.captions], { type: 'text/vtt' });
            const blobUrl = URL.createObjectURL(blob);
            blobUrlRef.current = blobUrl;

            return [{
                src: blobUrl,
                kind: 'subtitles',
                srclang: 'en',
                label: 'English',
                default: true,
            }];
        } else {
            return [{
                src: props.captions.src,
                kind: props.captions.kind || 'subtitles',
                srclang: props.captions.srclang || 'en',
                label: props.captions.label || 'English',
                default: props.captions.default || false
            }];
        }
    };

    const options: VideoJSOptions = {
        controls: true,
        responsive: true,
        fluid: true,
        autoplay: false,
        controlBar: {
            volumePanel: {
                inline: true
            },
            children: [
                'playToggle',
                'volumePanel',
                'currentTimeDisplay',
                'progressControl',
                'durationDisplay',
                'fullscreenToggle',
                'customCaptionsToggle'
            ]
        },
        sources: [{
            src: props.source,
            type: 'video/mp4'
        }],
        poster: props.poster,
        tracks: createTracks()
    };

    const onReady = props.onReady;

    React.useEffect(() => {
        if (!playerRef.current) {
            const videoElement = document.createElement("video-js");
            videoElement.classList.add('vjs-big-play-centered');
            if (videoRef.current) {
                videoRef.current.appendChild(videoElement);

                const player = playerRef.current = videojs(videoElement, options, () => {
                    onReady && onReady(player);
                });
            }
        } else {
            const player = playerRef.current;
            player.autoplay(options.autoplay);
            player.src(options.sources);

            const existingTracks = player.remoteTextTracks();
            for (let i = existingTracks.length - 1; i >= 0; i--) {
                player.removeRemoteTextTrack(existingTracks[i]);
            }

            options.tracks?.forEach(track => {
                player.addRemoteTextTrack({
                    kind: track.kind,
                    src: track.src,
                    srclang: track.srclang,
                    label: track.label,
                    default: track.default
                }, false);
            });
        }
    }, [props.source, props.captions]);

    React.useEffect(() => {
        return () => {
            if (playerRef.current && !playerRef.current.isDisposed()) {
                playerRef.current.dispose();
                playerRef.current = null;
            }

            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }
        };
    }, []);

    return (
        <div data-vjs-player>
            <div ref={videoRef} />
        </div>
    );
}

export default VideoJS;